import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  fetchAvailableDevices,
  fetchConfiguredDevices,
  addDeviceToMonitoring,
} from "../../store/devicesSlice";
import { IDevice } from "../../types";

interface DevicePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceAdded?: (device: IDevice) => void;
}

const DevicePickerModal: React.FC<DevicePickerModalProps> = ({
  isOpen,
  onClose,
  onDeviceAdded,
}) => {
  console.log("🟢 DevicePickerModal render - isOpen:", isOpen);
  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);
  const { available, loading, error } = useSelector(
    (state: RootState) => state.devices
  );

  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [deviceSettings, setDeviceSettings] = useState({
    displayName: "",
    color: "#3B82F6",
    icon: "device",
    showTrail: false,
  });
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualDevice, setManualDevice] = useState({
    mac: "",
    name: "",
  });

  // Track if component is mounted and if fetch has been initiated
  const isMountedRef = useRef(false);
  const hasFetchedRef = useRef<boolean>((window as any).__devicePickerHasFetched || false);

  // Fetch available devices when modal opens
  useEffect(() => {
    console.log(
      "🟢 DevicePickerModal useEffect - isOpen:",
      isOpen,
      "hasFetched:",
      hasFetchedRef.current
    );

    // Skip if not open or already fetched
    if (!isOpen || hasFetchedRef.current) {
      console.log("🟢 Skipping fetch");
      return;
    }

    // Mark as mounted and fetched
    isMountedRef.current = true;
    hasFetchedRef.current = true;
    (window as any).__devicePickerHasFetched = true;

    console.log("🟢 Fetching available devices...");
    // Fetch devices
    dispatch(fetchAvailableDevices());

    // Cleanup function
    return () => {
      console.log("🟢 DevicePickerModal cleanup");
      isMountedRef.current = false;
    };
  }, [dispatch, isOpen]);

  // Reset fetch flag when modal closes
  useEffect(() => {
    console.log("🟢 Reset effect - isOpen:", isOpen);
    if (!isOpen) {
      console.log("🟢 Resetting hasFetchedRef");
      hasFetchedRef.current = false;
    }
  }, [isOpen]);

  const handleSelectDevice = (device: IDevice) => {
    if (selectedDevice === device.mac) {
      setSelectedDevice(null);
      resetSettings();
    } else {
      setSelectedDevice(device.mac);
      setDeviceSettings({
        displayName: device.name || device.mac,
        color: "#3B82F6",
        icon: "device",
        showTrail: false,
      });
    }
  };

  const resetSettings = () => {
    setDeviceSettings({
      displayName: "",
      color: "#3B82F6",
      icon: "device",
      showTrail: false,
    });
  };

  const handleAddToMonitoring = async (device: IDevice) => {
    try {
      const result = await dispatch(
        addDeviceToMonitoring({
          mac: device.mac,
          settings: deviceSettings,
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        // Refresh configured devices to show the new device in the list
        await dispatch(fetchConfiguredDevices());
        onDeviceAdded?.(device);
        onClose();
        resetState();
      }
    } catch (error) {
      console.error("Failed to add device to monitoring:", error);
    }
  };

  const handleManualAdd = async () => {
    if (manualDevice.mac && manualDevice.name) {
      const device: IDevice = {
        mac: manualDevice.mac.toUpperCase(),
        name: manualDevice.name,
        isMonitored: false,
        isAvailableInAPI: false,
      };

      try {
        const result = await dispatch(
          addDeviceToMonitoring({
            mac: device.mac,
            settings: {
              displayName: manualDevice.name,
              color: deviceSettings.color,
              icon: deviceSettings.icon,
              showTrail: deviceSettings.showTrail,
            },
          })
        );

        if (result.meta.requestStatus === "fulfilled") {
          // Refresh configured devices to show the new device in the list
          await dispatch(fetchConfiguredDevices());
          onDeviceAdded?.(device);
          onClose();
          resetState();
        }
      } catch (error) {
        console.error("Failed to add manual device to monitoring:", error);
      }
    }
  };

  const resetState = () => {
    setSelectedDevice(null);
    setShowManualEntry(false);
    setManualDevice({ mac: "", name: "" });
    resetSettings();
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return language === "he" ? "לא זמין" : "N/A";
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffMinutes < 1) {
      return language === "he" ? "זה עתה" : "Just now";
    } else if (diffMinutes < 60) {
      return language === "he"
        ? `לפני ${Math.floor(diffMinutes)} דקות`
        : `${Math.floor(diffMinutes)} min ago`;
    } else if (diffMinutes < 24 * 60) {
      return language === "he"
        ? `לפני ${Math.floor(diffMinutes / 60)} שעות`
        : `${Math.floor(diffMinutes / 60)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!isOpen) {
    console.log("🟢 DevicePickerModal not rendering - isOpen is false");
    return null;
  }

  console.log("🟢 DevicePickerModal rendering JSX");
  const portalTarget = document.body || document.getElementById("root")!;
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2147483647] p-4 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {language === "he"
                ? "הוסף התקן למעקב"
                : "Add Device to Monitoring"}
            </h2>
            <button
              onClick={() => {
                onClose();
                resetState();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Toggle between Available and Manual */}
          {/* <div className="mt-4 flex space-x-2 rtl:space-x-reverse">
            <button
              onClick={() => setShowManualEntry(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                !showManualEntry
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {language === "he" ? "מ-API" : "From API"}
            </button>
            <button
              onClick={() => setShowManualEntry(true)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                showManualEntry
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {language === "he" ? "הוספה ידנית" : "Manual Entry"}
            </button>
          </div> */}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden overflow-y-scroll flex flex-col">
          {showManualEntry ? (
            /* Manual Entry Form */
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === "he" ? "כתובת MAC" : "MAC Address"}
                </label>
                <input
                  type="text"
                  value={manualDevice.mac}
                  onChange={(e) =>
                    setManualDevice({ ...manualDevice, mac: e.target.value })
                  }
                  placeholder="XX:XX:XX:XX:XX:XX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === "he" ? "שם התקן" : "Device Name"}
                </label>
                <input
                  type="text"
                  value={manualDevice.name}
                  onChange={(e) =>
                    setManualDevice({ ...manualDevice, name: e.target.value })
                  }
                  placeholder={
                    language === "he" ? "הכנס שם התקן" : "Enter device name"
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "צבע" : "Color"}
                  </label>
                  <input
                    type="color"
                    value={deviceSettings.color}
                    onChange={(e) =>
                      setDeviceSettings({
                        ...deviceSettings,
                        color: e.target.value,
                      })
                    }
                    className="w-full h-10 border border-gray-300 rounded cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "סמל" : "Icon"}
                  </label>
                  <select
                    value={deviceSettings.icon}
                    onChange={(e) =>
                      setDeviceSettings({
                        ...deviceSettings,
                        icon: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="device">Device</option>
                    <option value="phone">Phone</option>
                    <option value="tablet">Tablet</option>
                    <option value="laptop">Laptop</option>
                    <option value="watch">Watch</option>
                    <option value="tag">Tag</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showTrail"
                  checked={deviceSettings.showTrail}
                  onChange={(e) =>
                    setDeviceSettings({
                      ...deviceSettings,
                      showTrail: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="showTrail"
                  className="ml-2 text-sm text-gray-700"
                >
                  {language === "he" ? "הצג שובל תנועה" : "Show Movement Trail"}
                </label>
              </div>

              <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-4">
                <button
                  onClick={() => {
                    onClose();
                    resetState();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  {language === "he" ? "ביטול" : "Cancel"}
                </button>
                <button
                  onClick={handleManualAdd}
                  disabled={!manualDevice.mac || !manualDevice.name}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md transition-colors"
                >
                  {language === "he" ? "הוסף למעקב" : "Add to Monitoring"}
                </button>
              </div>
            </div>
          ) : (
            /* Available Devices List */
            <div className="flex-1 flex flex-col">
              {/* Device Settings Panel */}
              {selectedDevice && (
                <div className="p-4 bg-blue-50 border-b border-blue-200">
                  <h3 className="font-medium text-gray-900 mb-3">
                    {language === "he" ? "הגדרות התקן" : "Device Settings"}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === "he" ? "שם תצוגה" : "Display Name"}
                      </label>
                      <input
                        type="text"
                        value={deviceSettings.displayName}
                        onChange={(e) =>
                          setDeviceSettings({
                            ...deviceSettings,
                            displayName: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === "he" ? "צבע" : "Color"}
                      </label>
                      <input
                        type="color"
                        value={deviceSettings.color}
                        onChange={(e) =>
                          setDeviceSettings({
                            ...deviceSettings,
                            color: e.target.value,
                          })
                        }
                        className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === "he" ? "סמל" : "Icon"}
                      </label>
                      <select
                        value={deviceSettings.icon}
                        onChange={(e) =>
                          setDeviceSettings({
                            ...deviceSettings,
                            icon: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="device">Device</option>
                        <option value="phone">Phone</option>
                        <option value="tablet">Tablet</option>
                        <option value="laptop">Laptop</option>
                        <option value="watch">Watch</option>
                        <option value="tag">Tag</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showTrailModal"
                        checked={deviceSettings.showTrail}
                        onChange={(e) =>
                          setDeviceSettings({
                            ...deviceSettings,
                            showTrail: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor="showTrailModal"
                        className="ml-2 text-sm text-gray-700"
                      >
                        {language === "he" ? "הצג שובל" : "Show Trail"}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Device List */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">
                      {language === "he" ? "טוען..." : "Loading..."}
                    </p>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center text-red-600">
                    <p>{error}</p>
                  </div>
                ) : available.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="mt-2">
                      {language === "he"
                        ? "אין התקנים זמינים"
                        : "No devices available"}
                    </p>
                    <p className="text-sm mt-1">
                      {language === "he"
                        ? "התקנים יופיעו כאן לאחר סנכרון עם ה-API"
                        : "Devices will appear here after syncing with the API"}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {available.map((device) => {
                      const isSelected = selectedDevice === device.mac;
                      return (
                        <div
                          key={device.mac}
                          className={`p-4 cursor-pointer transition-colors duration-200 ${
                            isSelected
                              ? "bg-blue-50 border-l-4 border-blue-500"
                              : "hover:bg-gray-50"
                          }`}
                          onClick={() => handleSelectDevice(device)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">
                                {device.name || device.mac}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {device.mac}
                              </p>
                              <div className="flex items-center space-x-4 rtl:space-x-reverse mt-1">
                                <span className="text-xs text-green-600 font-medium">
                                  {language === "he"
                                    ? "זמין ב-API"
                                    : "Available in API"}
                                </span>
                                {device.lastSeenInAPI && (
                                  <span className="text-xs text-gray-500">
                                    {language === "he"
                                      ? "נראה לאחרונה:"
                                      : "Last seen:"}{" "}
                                    {formatLastSeen(device.lastSeenInAPI)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center">
                              {isSelected ? (
                                <svg
                                  className="w-5 h-5 text-blue-600"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                </svg>
                              ) : (
                                <svg
                                  className="w-5 h-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Footer Actions */}
        {selectedDevice && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end space-x-3 rtl:space-x-reverse">
              <button
                onClick={() => {
                  onClose();
                  resetState();
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
              >
                {language === "he" ? "ביטול" : "Cancel"}
              </button>
              <button
                onClick={() => {
                  const device = available.find(
                    (d) => d.mac === selectedDevice
                  );
                  if (device) handleAddToMonitoring(device);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                {language === "he" ? "הוסף למעקב" : "Add to Monitoring"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    portalTarget
  );
};

export default React.memo(DevicePickerModal);
